import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { Card, CardContent } from './ui/card';
import { Button } from './ui/button';
import { Layers, Download, Loader2, CreditCard, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';
import { API } from '../config/api';

const eur = (v) => `${Math.round(v || 0).toLocaleString('fr-FR')} €`;
const LEVELS = [
  { k: 'declaree', label: 'Déclarée (niv.1)' },
  { k: 'mandat_signe', label: 'Mandat signé (niv.2)' },
  { k: 'active', label: 'Payée / active (niv.3)' },
  { k: 'impayee', label: 'Impayée' },
  { k: 'resiliee', label: 'Résiliée' },
];

export default function EngagementDashboardPanel() {
  const [d, setD] = useState(null);

  const load = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/admin/engagement/dashboard`, { withCredentials: true });
      setD(res.data);
    } catch (e) { /* silencieux */ }
  }, []);
  useEffect(() => { load(); }, [load]);

  const downloadCsv = async () => {
    try {
      const res = await axios.get(`${API}/admin/engagement/export-csv`, { withCredentials: true, responseType: 'blob' });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement('a');
      a.href = url; a.download = `PURO_engagement_${new Date().toISOString().slice(0, 10)}.csv`; a.click();
      URL.revokeObjectURL(url);
      toast.success('Export CSV téléchargé.');
    } catch (e) { toast.error('Export impossible.'); }
  };

  if (!d) return (
    <Card className="border-[#E9DDCC] rounded-2xl"><CardContent className="p-6 flex justify-center"><Loader2 className="w-5 h-5 animate-spin text-[#C99A55]" /></CardContent></Card>
  );

  return (
    <Card className="border border-[#E9DDCC] rounded-2xl bg-white" data-testid="engagement-dashboard-panel">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <div className="flex items-center gap-2">
            <Layers className="w-4 h-4 text-[#C99A55]" strokeWidth={1.8} />
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#C99A55] font-semibold">Engagement</span>
          </div>
          <Button variant="outline" onClick={downloadCsv}
            className="rounded-full border-[#C99A55]/50 text-[#9A744C] hover:bg-[#C99A55] hover:text-white h-9"
            data-testid="engagement-csv-btn"><Download className="w-4 h-4 mr-1" /> Export CSV</Button>
        </div>
        <h3 className="font-heading text-lg text-[#493329]">Tableau de bord engagement (données Stripe)</h3>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <div className="bg-[#F5EFE5] rounded-xl p-3 text-center"><div className="text-[10px] uppercase text-[#AE9885]">Mandats signés</div><div className="font-heading text-2xl text-[#493329]" data-testid="eng-mandates">{d.mandates_signed}</div></div>
          <div className="bg-[#F5EFE5] rounded-xl p-3 text-center border border-[#C99A55]/40"><div className="text-[10px] uppercase text-[#C99A55]">CA mensuel engagé TTC</div><div className="font-heading text-2xl text-[#C99A55]" data-testid="eng-ca-ttc">{eur(d.ca_engaged_ttc)}</div></div>
          <div className="bg-[#F5EFE5] rounded-xl p-3 text-center"><div className="text-[10px] uppercase text-[#AE9885]">CA engagé HT</div><div className="font-heading text-2xl text-[#493329]" data-testid="eng-ca-ht">{eur(d.ca_engaged_ht)}</div></div>
          <div className="bg-[#F5EFE5] rounded-xl p-3 text-center"><div className="text-[10px] uppercase text-[#AE9885]">Inscrites sans mandat</div><div className="font-heading text-2xl text-[#C76542]" data-testid="eng-nomandate">{d.confirmed_without_mandate.count}</div></div>
        </div>

        {/* Niveaux */}
        <div className="grid grid-cols-2 md:grid-cols-5 gap-2 text-center">
          {LEVELS.map((l) => (
            <div key={l.k} className="bg-white border border-[#E9DDCC] rounded-lg p-2.5">
              <div className="text-[10px] uppercase text-[#AE9885]">{l.label}</div>
              <div className="font-heading text-xl text-[#493329]" data-testid={`eng-level-${l.k}`}>{d.levels[l.k]}</div>
            </div>
          ))}
        </div>

        {/* Répartition formule */}
        <div className="flex flex-wrap gap-1.5" data-testid="eng-formules">
          {d.by_formule.map((f) => (
            <span key={f.formule} className="text-[11px] bg-[#F5EFE5] border border-[#E9DDCC] rounded-full px-2.5 py-0.5 text-[#493329]">{f.formule} : <strong>{f.count}</strong></span>
          ))}
        </div>

        {/* Listes sans mandat / impayés */}
        <div className="grid md:grid-cols-2 gap-4">
          <div className="rounded-xl border border-[#E9DDCC] p-3">
            <div className="flex items-center gap-1.5 text-sm font-medium text-[#493329] mb-2"><CreditCard className="w-4 h-4 text-[#C99A55]" /> Confirmées sans mandat ({d.confirmed_without_mandate.count})</div>
            <ul className="text-xs text-[#7A6455] space-y-0.5 max-h-40 overflow-y-auto">
              {d.confirmed_without_mandate.items.map((i) => <li key={i.email}>• {i.name || i.email} {i.plan ? `— ${i.plan}` : '— sans formule'}</li>)}
              {d.confirmed_without_mandate.count === 0 && <li className="text-[#AE9885]">Aucune.</li>}
            </ul>
          </div>
          <div className="rounded-xl border border-red-200 bg-red-50/40 p-3">
            <div className="flex items-center gap-1.5 text-sm font-medium text-red-700 mb-2"><AlertTriangle className="w-4 h-4" /> Impayés ({d.impayes.count})</div>
            <ul className="text-xs text-[#7A6455] space-y-0.5 max-h-40 overflow-y-auto">
              {d.impayes.items.map((i) => <li key={i.email}>• {i.name || i.email}</li>)}
              {d.impayes.count === 0 && <li className="text-[#AE9885]">Aucun impayé.</li>}
            </ul>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
